import { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import api, { getErrorMessage } from '../utils/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Package, Plus, PencilSimple, Trash, Barcode } from '@phosphor-icons/react';
import BarcodeScanButton from '../components/BarcodeScanButton';

const StockItems = () => {
  const [items, setItems] = useState([]);
  const [filteredItems, setFilteredItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [filters, setFilters] = useState({
    search: '',
    category: 'all',
    unit: 'all'
  });
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    barcode: '',
    unit: 'piece',
    category: '',
    min_quantity: 5,
    purchase_price: 0
  });

  const CATEGORIES = [
    'Thermal Bed',
    'Mat',
    'Massage Chair',
    'Ionizer Water',
    'Accessories',
    'Other'
  ];

  const UNITS = ['piece', 'set', 'box', 'unit'];

  useEffect(() => {
    fetchItems();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [items, filters]);

  const fetchItems = async () => {
    try {
      const response = await api.get('/stock/items');
      setItems(response.data);
      setFilteredItems(response.data);
    } catch (error) {
      toast.error('Failed to load stock items');
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...items];

    // Search filter (name, barcode, description)
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      filtered = filtered.filter(item =>
        item.name?.toLowerCase().includes(searchLower) ||
        item.barcode?.toLowerCase().includes(searchLower) ||
        item.description?.toLowerCase().includes(searchLower) ||
        item.category?.toLowerCase().includes(searchLower)
      );
    }

    // Category filter
    if (filters.category && filters.category !== 'all') {
      filtered = filtered.filter(item => item.category === filters.category);
    }

    // Unit filter
    if (filters.unit && filters.unit !== 'all') {
      filtered = filtered.filter(item => item.unit === filters.unit);
    }

    setFilteredItems(filtered);
  };

  const resetFilters = () => {
    setFilters({
      search: '',
      category: 'all',
      unit: 'all'
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (selectedItem) {
        await api.put(`/stock/items/${selectedItem.item_id}`, formData);
        toast.success('Item updated successfully!');
        setShowEditDialog(false);
      } else {
        await api.post('/stock/items', formData);
        toast.success('Item created successfully!');
        setShowAddDialog(false);
      }
      setFormData({ name: '', description: '', barcode: '', unit: 'piece', category: '', min_quantity: 5, purchase_price: 0 });
      setSelectedItem(null);
      fetchItems();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Operation failed'));
    }
  };

  const handleEdit = (item) => {
    setSelectedItem(item);
    setFormData({
      name: item.name,
      description: item.description || '',
      barcode: item.barcode,
      unit: item.unit,
      category: item.category,
      min_quantity: item.min_quantity
    });
    setShowEditDialog(true);
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`Delete "${item.name}"?`)) return;
    
    try {
      await api.delete(`/stock/items/${item.item_id}`);
      toast.success('Item deleted successfully!');
      fetchItems();
    } catch (error) {
      toast.error('Failed to delete item');
    }
  };

  return (
    <Layout>
      <div className="space-y-6" data-testid="stock-items-page">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-[#2C4C3B]">Stock Items Master</h1>
            <p className="text-[#1C1C1A]/60">Manage jade health products catalog</p>
          </div>
          <Button
            onClick={() => setShowAddDialog(true)}
            className="bg-[#2C4C3B] hover:bg-[#2C4C3B]/90"
            data-testid="add-item-button"
          >
            <Plus size={20} weight="bold" className="mr-2" />
            Add Stock Item
          </Button>
        </div>

        {/* Filters Section */}
        {items.length > 0 && (
          <Card className="p-4 mb-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {/* Search Filter */}
              <div className="lg:col-span-2">
                <Label className="text-sm font-medium text-[#2C4C3B] mb-2">Search</Label>
                <Input
                  placeholder="Search by name, barcode, description..."
                  value={filters.search}
                  onChange={(e) => setFilters({...filters, search: e.target.value})}
                  className="w-full"
                />
              </div>

              {/* Category Filter */}
              <div>
                <Label className="text-sm font-medium text-[#2C4C3B] mb-2">Category</Label>
                <Select value={filters.category} onValueChange={(val) => setFilters({...filters, category: val})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Categories</SelectItem>
                    {CATEGORIES.map(cat => (
                      <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Unit Filter */}
              <div>
                <Label className="text-sm font-medium text-[#2C4C3B] mb-2">Unit</Label>
                <Select value={filters.unit} onValueChange={(val) => setFilters({...filters, unit: val})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Units</SelectItem>
                    {UNITS.map(unit => (
                      <SelectItem key={unit} value={unit}>{unit}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Filter Summary & Reset */}
            <div className="flex flex-wrap items-center justify-between mt-4 pt-4 border-t border-[#E5E5DF]">
              <div className="text-sm text-[#1C1C1A]/60">
                Showing <span className="font-semibold text-[#2C4C3B]">{filteredItems.length}</span> of <span className="font-semibold">{items.length}</span> items
                {(filters.search || filters.category !== 'all' || filters.unit !== 'all') && (
                  <span className="ml-2 text-orange-600">(filtered)</span>
                )}
              </div>
              {(filters.search || filters.category !== 'all' || filters.unit !== 'all') && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={resetFilters}
                  className="text-xs"
                >
                  Clear Filters
                </Button>
              )}
            </div>
          </Card>
        )}


        {loading ? (
          <div className="text-center py-12">Loading...</div>
        ) : items.length === 0 ? (
          <Card className="p-12 text-center">
            <Package size={64} className="mx-auto mb-4 text-[#1C1C1A]/20" />
            <h3 className="text-xl font-semibold mb-2">No Stock Items</h3>
            <p className="text-[#1C1C1A]/60 mb-4">Add your first stock item to get started</p>
            <Button onClick={() => setShowAddDialog(true)} className="bg-[#2C4C3B]">
              <Plus size={20} className="mr-2" />
              Add Item
            </Button>
          </Card>
        ) : filteredItems.length === 0 ? (
          <Card className="p-12 text-center">
            <Package size={64} className="mx-auto mb-4 text-[#1C1C1A]/20" />
            <h3 className="text-xl font-semibold mb-2">No items match your filters</h3>
            <p className="text-[#1C1C1A]/60 mb-4">Try adjusting your search or filters</p>
            <Button onClick={resetFilters} variant="outline">
              Clear Filters
            </Button>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredItems.map((item) => (
              <Card key={item.item_id} className="p-6 hover:shadow-lg transition-shadow">
                <div className="flex justify-between items-start mb-4">
                  <div className="flex-1">
                    <h3 className="font-semibold text-lg text-[#2C4C3B]">{item.name}</h3>
                    <p className="text-sm text-[#1C1C1A]/60">{item.category}</p>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleEdit(item)}
                      data-testid={`edit-item-${item.item_id}`}
                    >
                      <PencilSimple size={16} />
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleDelete(item)}
                      className="text-red-600 hover:text-red-700"
                    >
                      <Trash size={16} />
                    </Button>
                  </div>
                </div>
                
                <div className="space-y-2 text-sm">
                  <div className="flex items-center gap-2 text-[#1C1C1A]/80">
                    <Barcode size={16} />
                    <span className="font-mono">{item.barcode}</span>
                  </div>
                  {item.description && (
                    <p className="text-[#1C1C1A]/60">{item.description}</p>
                  )}
                  <div className="flex justify-between pt-2 border-t border-[#E5E5DF]">
                    <span className="text-[#1C1C1A]/60">Unit:</span>
                    <span className="font-medium">{item.unit}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-[#1C1C1A]/60">Purchase Price:</span>
                    <span className="font-medium text-green-600">₹{item.purchase_price || 0}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-[#1C1C1A]/60">Min Quantity:</span>
                    <span className="font-medium text-orange-600">{item.min_quantity}</span>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}

        {/* Add Item Dialog */}
        <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
          <DialogContent className="bg-white max-w-md">
            <DialogHeader>
              <DialogTitle className="text-2xl font-semibold text-[#2C4C3B]">Add Stock Item</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label>Item Name *</Label>
                <Input
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  placeholder="Thermal Bed X1000"
                  data-testid="item-name-input"
                />
              </div>
              
              <div>
                <Label>Category *</Label>
                <Select value={formData.category} onValueChange={(val) => setFormData({...formData, category: val})}>
                  <SelectTrigger data-testid="category-select">
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map(cat => (
                      <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Barcode *</Label>
                <div className="flex gap-2">
                  <Input
                    required
                    value={formData.barcode}
                    onChange={(e) => setFormData({...formData, barcode: e.target.value})}
                    placeholder="Scan or enter barcode"
                    data-testid="barcode-input"
                    className="flex-1"
                  />
                  <BarcodeScanButton 
                    onScan={(code) => setFormData({...formData, barcode: code})}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Unit *</Label>
                  <Select value={formData.unit} onValueChange={(val) => setFormData({...formData, unit: val})}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {UNITS.map(unit => (
                        <SelectItem key={unit} value={unit}>{unit}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Min Quantity *</Label>
                  <Input
                    type="number"
                    required
                    min="0"
                    value={formData.min_quantity}
                    onChange={(e) => setFormData({...formData, min_quantity: parseInt(e.target.value)})}
                  />
                </div>
              </div>

              <div>
                <Label>Purchase Price (Default)</Label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={formData.purchase_price}
                  onChange={(e) => setFormData({...formData, purchase_price: parseFloat(e.target.value) || 0})}
                  placeholder="Enter default purchase price"
                />
              </div>

              <div>
                <Label>Description</Label>
                <Input
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  placeholder="Optional description"
                />
              </div>

              <div className="flex gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => setShowAddDialog(false)} className="flex-1">
                  Cancel
                </Button>
                <Button type="submit" className="flex-1 bg-[#2C4C3B]">
                  Create Item
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Edit Item Dialog */}
        <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
          <DialogContent className="bg-white max-w-md">
            <DialogHeader>
              <DialogTitle className="text-2xl font-semibold text-[#2C4C3B]">Edit Stock Item</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label>Item Name *</Label>
                <Input
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                />
              </div>
              
              <div>
                <Label>Category *</Label>
                <Select value={formData.category} onValueChange={(val) => setFormData({...formData, category: val})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map(cat => (
                      <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Barcode *</Label>
                <Input
                  required
                  value={formData.barcode}
                  onChange={(e) => setFormData({...formData, barcode: e.target.value})}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Unit *</Label>
                  <Select value={formData.unit} onValueChange={(val) => setFormData({...formData, unit: val})}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {UNITS.map(unit => (
                        <SelectItem key={unit} value={unit}>{unit}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Min Quantity *</Label>
                  <Input
                    type="number"
                    required
                    min="0"
                    value={formData.min_quantity}
                    onChange={(e) => setFormData({...formData, min_quantity: parseInt(e.target.value)})}
                  />
                </div>
              </div>

              <div>
                <Label>Purchase Price (Default)</Label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={formData.purchase_price}
                  onChange={(e) => setFormData({...formData, purchase_price: parseFloat(e.target.value) || 0})}
                  placeholder="Enter default purchase price"
                />
              </div>

              <div>
                <Label>Description</Label>
                <Input
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                />
              </div>

              <div className="flex gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => setShowEditDialog(false)} className="flex-1">
                  Cancel
                </Button>
                <Button type="submit" className="flex-1 bg-[#2C4C3B]">
                  Update Item
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default StockItems;
